import { PERMISSIONS } from "@/constants/permissions"
import { invalidateDeclaration } from "@/features/material-compliance/api/declarationsApi"
import { MemoryBecause } from "@/features/material-compliance/components/memory/MemoryBecause"
import { MemoryRating } from "@/features/material-compliance/components/memory/MemoryRating"
import { MemoryTrail } from "@/features/material-compliance/components/memory/MemoryTrail"
import { useConflictResolution } from "@/features/material-compliance/components/memory/useConflictResolution"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useQueryClient } from "@tanstack/react-query"
import { AlertTriangle, Check, Sparkles } from "lucide-react"
import { useEffect, useRef } from "react"

import { useRecordMemoryShown } from "../../../../../api/memoryApi"
import type { ConcentrationMemoryHint } from "../../../../../api/types"

export interface MemoryAnnotationProps {
  declId: string
  hint: ConcentrationMemoryHint
}

/**
 * A remembered concentration, offered beside the cell it is about (Turnus Memory #1).
 *
 * PURELY ADVISORY. Nothing is applied on render, on mount, or on any click — this component
 * has no write path at all. A reviewer reads the remembered number and types whatever they
 * decide into the cell, through the ordinary save the input already owns.
 *
 * THAT IS A DELIBERATE NARROWING. The earlier "Use this" button wrote the value directly,
 * which conflated two separate questions — "put this number in the box" and "is this memory
 * right" — into one click. It also meant memory only ever learned from someone who happened
 * to want the value at that moment: a reviewer who read the suggestion, agreed, and typed
 * the number themselves taught it nothing. The thumbs (see `MemoryRating`) now carry the
 * second question on their own, and a thumbs-down is still the revert that retires a wrong
 * entry after three — the only way a bad correction stops haunting the org (scenario #7).
 *
 * A SIDE EFFECT WORTH NAMING: because nothing is written from here, the unit problem that
 * `value` existed to guard is gone. The cell stores a number only when `float()` accepts it,
 * so applying a remembered "35 ppm" into a per-cent cell was wrong by ten thousand. Now the
 * reviewer reads `display` — the correctly rendered compliance number, unit and all — and
 * types what belongs there. `value` still travels for the conflict sides, which show the
 * same two shapes.
 *
 * TWO NUMBERS THAT DISAGREE ARE SHOWN AS TWO. When `conflict` is non-empty there is no
 * single remembered value and `entryId`/`display`/`value` are all null — so that branch is
 * checked before anything reads them. Each side is rated independently and none is marked
 * as the right one, because the system does not decide (Ferdi's #6b).
 *
 * A PERSON CAN STILL SETTLE IT, which is different from the system picking. Clicking a side
 * highlights it; confirming calls `resolve-conflict`, which retires every other side server
 * side. Once that lands, this renders the confirmed side as an ordinary suggestion — the
 * contradiction is gone, not just hidden, and a page reload shows the same thing.
 */
export function MemoryAnnotation({ declId, hint }: MemoryAnnotationProps) {
  const { translate } = useTranslation()
  const recordShown = useRecordMemoryShown()
  const queryClient = useQueryClient()
  const conflictResolution = useConflictResolution({
    surface: hint.surface,
    targetType: "declaration",
    targetId: declId,
    permission: PERMISSIONS.MC_DECLARATIONS_REVIEW,
    invalidate: () => invalidateDeclaration(queryClient, declId),
  })

  const { entryId } = hint
  const cell = `${declId}:${hint.articleNumber}:${hint.substanceCas ?? ""}`
  const allSides = hint.conflict ?? []
  // A resolved side never reappears, and the winner is rendered through the single-value
  // branch below — this list is only what is STILL contested.
  const sides = allSides.filter(
    (s) => !conflictResolution.retiredEntryIds.includes(s.entryId),
  )
  // A FOREIGN SIDE MEANS A NEW CONFLICT, and the latch from the last one must not survive
  // it — see `reset`'s own note in the hook. Checked and applied during render (not an
  // effect): by the time anything below reads `resolvedWinnerId`, it must already reflect
  // this conflict, not the previous one.
  if (
    conflictResolution.resolvedWinnerId &&
    allSides.some(
      (s) =>
        s.entryId !== conflictResolution.resolvedWinnerId &&
        !conflictResolution.retiredEntryIds.includes(s.entryId),
    )
  ) {
    conflictResolution.reset()
  }

  const winner = conflictResolution.resolvedWinnerId
    ? allSides.find((s) => s.entryId === conflictResolution.resolvedWinnerId)
    : undefined
  // `!winner` FIRST, ALWAYS. After a resolve, `sides` is legitimately down to one (the
  // retired side filtered out above), but `winner` is truthy by then, so this short-circuits
  // before `sides.length > 1` is even checked and the single-value branch renders normally.
  // `> 1` rather than `> 0`: a real conflict can still arrive here with one side lost
  // upstream (tier, `_not_supported_by`, or the bitemporal window) — the server already
  // withholds that case where it can (see `memory.read.conflict_one_sided`), but this is the
  // backstop. Rendering that lone survivor as an ordinary suggestion would be the UI picking
  // a winner in a contradiction nobody resolved, which is worse than showing nothing.
  const isConflict = !winner && sides.length > 1
  const renderable = isConflict || winner || (!!entryId && !!hint.display)

  // EVERY SIDE OF A CONFLICT IS ITS OWN SIGHTING — both were put in front of somebody, and
  // each one's accept rate is only meaningful against the number of times it was shown.
  const shownIds = isConflict
    ? sides.map((s) => s.entryId)
    : entryId
      ? [entryId]
      : []
  const sighting = shownIds.length ? `${cell}:${shownIds.join(",")}` : null

  // Reported once per ENTRY ID, not once per sighting STRING. A resolve changes the shape
  // of the composite key for this same cell — `cell:a,b` while still conflicted becomes
  // `cell:a` the moment the resolution's refetch lands, because the winner now renders
  // through the ordinary single-value branch. Comparing only the latest key would read that
  // as a brand new sighting and report the winner shown a second time for one continuous
  // look, inflating the denominator of the one metric this feature is judged on. Tracked per
  // cell so a genuinely different field still starts its own set.
  const reportedIds = useRef<{ cell: string; ids: Set<string> }>({
    cell,
    ids: new Set(),
  })
  useEffect(() => {
    if (!renderable || shownIds.length === 0) return
    if (reportedIds.current.cell !== cell) {
      reportedIds.current = { cell, ids: new Set() }
    }
    const unseen = shownIds.filter((id) => !reportedIds.current.ids.has(id))
    if (unseen.length === 0) return
    unseen.forEach((id) => reportedIds.current.ids.add(id))
    recordShown.mutate(
      unseen.map((id) => ({
        entryId: id,
        surface: hint.surface,
        targetType: "declaration",
        targetId: declId,
      })),
    )
    // recordShown is a stable mutation handle; re-running on it would re-report.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderable, cell, sighting, hint.surface, declId])

  if (!renderable) return null

  if (isConflict) {
    return (
      <div
        className="mt-1 flex flex-col gap-1 rounded-md border border-attention bg-grey2 px-2 py-1.5 text-left"
        data-testid={`memory-concentration-conflict-${hint.articleNumber}`}
      >
        <div className="flex items-start gap-1.5">
          <AlertTriangle
            size={11}
            className="mt-[3px] shrink-0 text-attention"
            aria-hidden="true"
            data-testid="memory-conflict-icon"
          />
          <div className="flex flex-col">
            <p className="text-[10px] tracking-wide text-attention uppercase">
              {translate("MC_MEMORY_CONFLICT_TITLE")}
            </p>
            <p className="text-[10px] text-grey5">
              {translate("MC_MEMORY_CONFLICT_HINT")}
            </p>
          </div>
        </div>
        {/* NEITHER SIDE IS MARKED as the recommendation. The server ranked them for a
            stable display order, not as advice — but a person can still select one to
            highlight it, then confirm it, which is a decision rather than a guess.
            SELECTION IS ITS OWN BUTTON, not a click handler on the whole card: the card
            already hosts the rating thumbs, the trail toggle and (once selected) the
            confirm button, and a click anywhere in that tree bubbles. A card-level
            onClick would re-select the row every time one of those is used instead of
            doing what it was clicked for — and a card-level onKeyDown catches the SAME
            Enter/Space the nested buttons need, so keyboard users could never reach
            Confirm at all. */}
        {sides.map((side) => {
          const selected = conflictResolution.selectedEntryId === side.entryId
          return (
            <div
              key={side.entryId}
              className={`flex flex-col gap-1 rounded border px-1.5 py-1 text-left transition-colors ${
                selected ? "border-attention bg-grey3" : "border-grey4"
              }`}
              data-testid={`memory-conflict-side-${side.entryId}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex min-w-0 items-start gap-1.5">
                  <button
                    type="button"
                    aria-pressed={selected}
                    aria-label={`${translate("MC_MEMORY_CONFLICT_SELECT")}: ${side.display}`}
                    onClick={() => conflictResolution.select(side.entryId)}
                    className={`mt-[4px] size-[11px] shrink-0 rounded-full border transition-colors ${
                      selected
                        ? "border-attention bg-attention"
                        : "border-grey5 hover:border-white"
                    }`}
                    data-testid={`memory-conflict-select-${side.entryId}`}
                  />
                  <div className="flex min-w-0 flex-col">
                    <p className="text-[12px] font-semibold text-white">
                      {side.display}
                    </p>
                    <MemoryBecause
                      origin={side.origin}
                      surface="concentration"
                      fallback={side.because}
                      testId={side.entryId}
                    />
                    <MemoryTrail
                      origin={side.origin}
                      testId={side.entryId}
                      entryId={side.entryId}
                      surface={hint.surface}
                    />
                  </div>
                </div>
                <MemoryRating
                  target={{
                    entryId: side.entryId,
                    surface: hint.surface,
                    targetType: "declaration",
                    targetId: declId,
                  }}
                  permission={PERMISSIONS.MC_DECLARATIONS_REVIEW}
                  testId={side.entryId}
                  subjectLabel={side.display ?? undefined}
                />
              </div>
              {selected ? (
                <button
                  type="button"
                  disabled={conflictResolution.isConfirming}
                  onClick={() => conflictResolution.confirm(side.entryId)}
                  className="flex items-center justify-center gap-1 self-start rounded bg-attention px-2 py-0.5 text-[10px] font-semibold text-black transition-opacity disabled:opacity-60"
                  data-testid={`memory-confirm-correct-${side.entryId}`}
                >
                  <Check size={11} aria-hidden="true" />
                  {translate("MC_MEMORY_CONFIRM_CORRECT")}
                </button>
              ) : null}
            </div>
          )
        })}
      </div>
    )
  }

  const winnerDisplay = winner ? winner.display : hint.display
  const winnerOrigin = winner ? winner.origin : hint.origin
  const winnerBecause = winner ? winner.because : hint.because
  const winnerEntryId = winner ? winner.entryId : entryId

  return (
    <div
      className="mt-1 flex flex-col gap-1 rounded-md border border-grey4 bg-grey2 px-2 py-1.5 text-left"
      data-testid={`memory-concentration-${hint.articleNumber}`}
    >
      <div className="flex items-start justify-between gap-1.5">
        <div className="flex min-w-0 items-start gap-1.5">
          <Sparkles
            size={11}
            className="mt-[3px] shrink-0 text-attention"
            aria-hidden="true"
            data-testid="memory-icon"
          />
          <div className="flex min-w-0 flex-col">
            <p className="text-[10px] tracking-wide text-grey5 uppercase">
              {translate("MC_MEMORY_SUGGESTS")}
            </p>
            <p className="text-[12px] font-semibold text-white">
              {winnerDisplay}
            </p>
            <MemoryBecause
              origin={winnerOrigin}
              surface="concentration"
              fallback={winnerBecause}
              testId={hint.articleNumber}
            />
            <MemoryTrail
              origin={winnerOrigin}
              testId={hint.articleNumber}
              entryId={winnerEntryId}
              surface={hint.surface}
            />
          </div>
        </div>
        {winnerEntryId ? (
          <MemoryRating
            target={{
              entryId: winnerEntryId,
              surface: hint.surface,
              targetType: "declaration",
              targetId: declId,
            }}
            permission={PERMISSIONS.MC_DECLARATIONS_REVIEW}
            testId={hint.articleNumber}
          />
        ) : null}
      </div>
    </div>
  )
}
